import { useQuery } from '@tanstack/react-query';

import { accountStatementsService } from '@/services/accountStatements.service';
import type { AccountStatementFilters } from '@/types/accountStatements';

/**
 * Account Statements Hooks
 * React Query hooks for /api/v1/account-statements/* endpoints
 */

// Query keys
export const accountStatementKeys = {
  all: ['account-statements'] as const,
  lists: () => [...accountStatementKeys.all, 'list'] as const,
  list: (filters: AccountStatementFilters) => [...accountStatementKeys.lists(), filters] as const,
  detail: (id: string) => [...accountStatementKeys.all, 'detail', id] as const,
  signedUrl: (id: string) => [...accountStatementKeys.all, 'signed-url', id] as const,
};

/**
 * Hook to fetch account statements with filters (paginated)
 */
export const useAccountStatements = (filters: AccountStatementFilters = {}) => {
  return useQuery({
    queryKey: accountStatementKeys.list(filters),
    queryFn: () => accountStatementsService.getStatements(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch a single account statement
 */
export const useAccountStatement = (id: string) => {
  return useQuery({
    queryKey: accountStatementKeys.detail(id),
    queryFn: () => accountStatementsService.getStatement(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to get signed URL for PDF download
 */
export const useSignedUrl = (id: string, enabled = false) => {
  return useQuery({
    queryKey: accountStatementKeys.signedUrl(id),
    queryFn: () => accountStatementsService.getSignedUrl(id),
    enabled: enabled && !!id,
    staleTime: 50 * 60 * 1000, // 50 minutes (URLs expire in 1 hour)
  });
};
